import {get} from 'axios';
import he from 'he';
import { 
	RESPONSE_STATUS_FIELD,
	RESPONSE_DATA_SUBSET_FIELD,
	RESPONSE_FIELD,
	SEARCH_ENDPOINT_DECISIONS,
	SEARCH_ENDPOINT_FILINGS,
	SEARCH_ENDPOINT_DOCKETS,
	SEARCH_ENDPOINT_RECORDATIONS,
	SEARCH_ENDPOINT_BY_DATE,
	SEARCH_ENDPOINT_SERVICE_LIST,
	RESPONSE_DATA_FIELD,
	DECODE_SEARCH_RESULT_BODY,
	SEARCH_ENDPOINT_RECORDATION_PARTIES, 
	SEARCH_ENDPOINT_ENVIRONMENTAL_COMMENTS,
	SEARCH_ENDPOINT_ALL_DOCUMENTS
} from '../constants/api';
import { SEARCH_FOR_NAME, DATE_FIELDS, ENHANCED_SEARCH_TEXT_FIELDS } from '../constants/fields';
import { 
	DECISIONS_SEARCH_FOR_OPTION,
	FILINGS_SEARCH_FOR_OPTION,
	DOCKETS_SEARCH_FOR_OPTION,
	RECORDATIONS_SEARCH_FOR_OPTION,
	BY_DATE_SEARCH_FOR_OPTION,
	SERVICE_LIST_SEARCH_FOR_OPTION,
	RECORDATION_PARTIES_SEARCH_FOR_OPTION,
	SEARCH_FOR_ASSOCIATED_OPTION,
	ENVIRONMENTAL_COMMENTS_SEARCH_FOR_OPTION,
	getValidQueryParamFields,
	isValidQueryParam,
	ALL_DOCUMENTS_SEARCH_FOR_OPTION
} from '../constants/searches';
import { getYMDfromMDYFormat } from './dateUtil';

export function isSuccessResponse(response) {
	var success = false;
	if(response){
		if (RESPONSE_STATUS_FIELD in response) {
			if (
				response[RESPONSE_STATUS_FIELD] >= 200 &&
				response[RESPONSE_STATUS_FIELD] < 300
			) {
				success = true;
			}
		}
	}
	return success;
};

export function responseHasDataSubset(response) {
	var valid = false;
	
	if (RESPONSE_DATA_FIELD in response && 
		typeof response[RESPONSE_DATA_FIELD] === "object" &&
		RESPONSE_DATA_SUBSET_FIELD in response[RESPONSE_DATA_FIELD]
	){
		valid = true;
	}
	return valid;
};

export function responseObjectHasSearchFor(responseObj) {
	if (SEARCH_FOR_NAME in responseObj && responseObj[SEARCH_FOR_NAME]) {
		return true;
	} else {
		return false;
	}
};

export function getDataAndTypeFromResponseAndType(responseObj) {
	var responseData = null;
	if(responseObj){
		if (isSuccessResponse(responseObj[RESPONSE_FIELD])) {
			if(
				responseHasDataSubset(responseObj[RESPONSE_FIELD]) &&
				responseObjectHasSearchFor(responseObj)
			) {
				responseData = {
					searchFor: responseObj[SEARCH_FOR_NAME],
					data: responseObj[RESPONSE_FIELD][RESPONSE_DATA_FIELD]
				};
			}
		}
	}
	return responseData;
};

//   GETTING SEARCH RESULTS
const getSearchEndpoint = searchFor => {
	let searchEndpoint;
	switch (searchFor) {
		case DECISIONS_SEARCH_FOR_OPTION:
			searchEndpoint = SEARCH_ENDPOINT_DECISIONS;
			break;
		case FILINGS_SEARCH_FOR_OPTION:
			searchEndpoint = SEARCH_ENDPOINT_FILINGS;
			break;
		case DOCKETS_SEARCH_FOR_OPTION:
			searchEndpoint = SEARCH_ENDPOINT_DOCKETS;
			break;
		case RECORDATIONS_SEARCH_FOR_OPTION:
			searchEndpoint = SEARCH_ENDPOINT_RECORDATIONS;
			break;
		case RECORDATION_PARTIES_SEARCH_FOR_OPTION:
			searchEndpoint = SEARCH_ENDPOINT_RECORDATION_PARTIES;
			break;
		case BY_DATE_SEARCH_FOR_OPTION:
			searchEndpoint = SEARCH_ENDPOINT_BY_DATE;
			break;
		case SERVICE_LIST_SEARCH_FOR_OPTION:
			searchEndpoint = SEARCH_ENDPOINT_SERVICE_LIST;
			break;
		case ENVIRONMENTAL_COMMENTS_SEARCH_FOR_OPTION:
			searchEndpoint = SEARCH_ENDPOINT_ENVIRONMENTAL_COMMENTS;
			break;
		case ALL_DOCUMENTS_SEARCH_FOR_OPTION:
			searchEndpoint = SEARCH_ENDPOINT_ALL_DOCUMENTS;
			break;
		default:
			searchEndpoint = null;
	}
	return searchEndpoint;
};

const getSearchCriteria = fields => {
	var searchCriteria = {};
	for (var field in fields) {
		if (fields[field]) {
			if(DATE_FIELDS.includes(field)){
				let utcDateString = getYMDfromMDYFormat(fields[field]);
				let dateValue = new Date(utcDateString || fields[field]);
				if(!isNaN(dateValue)){
					searchCriteria[field] = dateValue;
				}
			} else{
				searchCriteria[field] = fields[field];
			}
		}
	}
	return JSON.stringify(searchCriteria);
};

export const getSearchResults = async ({ searchFor, fields, batchSize, startIndex }, handleSaveData) => {
	const searchEndpoint = getSearchEndpoint(searchFor);
	const searchCriteria = getSearchCriteria(fields);
	const params = {};
	var response = null;
	if (batchSize !== 0) {
		params.limit = batchSize;
	}
	if (startIndex) {
		params.startIndex = startIndex;
	}
	if (searchCriteria.length) {
		params.searchCriteria = searchCriteria;
	}
	
	response = await get(searchEndpoint, {
		params: params
	})
	.then(response => {
		if (DECODE_SEARCH_RESULT_BODY === true && response.data && response.data.dataSubset) {
			let dataDecoded = response.data.dataSubset;
			dataDecoded.forEach((data, idx) => {
				Object.entries(data).forEach(([key, value]) => {
					if(value !== null && value !== undefined && ENHANCED_SEARCH_TEXT_FIELDS.includes(key)) {
						data[key] = he.decode(value);
					}
				});
				dataDecoded[idx] = data;
			});
			response.data.dataSubset = dataDecoded;
		}
		return response;
	})
	.catch(response => {
		console.log("Error getting search results:");
		console.log(response);
		return response;
	});
	if (handleSaveData) {
		handleSaveData(response);
	}
	return response;
};

export const getSearchResultsByDate = async (
	{ fields, batchSize, startIndex },
	searches
) => {
	var responseObjects = [];
	for (let i in searches) {
		let searchFor = searches[i].searchFor;
		let searchFields = {...fields, ...searches[i].fields};
		let response = await getSearchResults({
			searchFor,
			fields: searchFields,
			batchSize,
			startIndex
		});
		responseObjects.push({ searchFor: searchFor, response: response });
	}
	return responseObjects;
};

export const getSearchResultsByFields = async (
	{ searchFor, fields, batchSize, startIndex },
	handleSaveData
) => {
	let responseObjects = [];
	
	let response = await getSearchResults(
		{ searchFor, fields, batchSize, startIndex },
		handleSaveData
	);
	responseObjects.push({ searchFor: searchFor, response: response });
	return responseObjects;
};

export const getSearchResultsByLimit = async (
	{ searchFor, batchSize, startIndex },
	handleSaveData
) => {
	return await getSearchResults(
		{ searchFor, batchSize, startIndex },
		handleSaveData
	);
};

export const getQueryParams = (lockedTo, defaultFields) => {
	var query = getQuery();
	var searchFor = DECISIONS_SEARCH_FOR_OPTION;
	
	if(!lockedTo){
		var queryParamSearchFor = query.get(SEARCH_FOR_NAME);
		
		if(queryParamSearchFor in SEARCH_FOR_ASSOCIATED_OPTION){
			searchFor = SEARCH_FOR_ASSOCIATED_OPTION[queryParamSearchFor];
		}
	} else {
		searchFor = lockedTo;
	}

	var searchFields = {...defaultFields};
	var queryParamFields = getValidQueryParamFields(searchFor);
	for(let i in queryParamFields){
		let fieldName = queryParamFields[i];
		let queryParam = query.get(fieldName);
		
		if(queryParam && !searchFields[fieldName]){
			if(isValidQueryParam(fieldName, queryParam)){
				searchFields[fieldName] = queryParam;
			}
		}
	}
	return [searchFor, searchFields];
};

export const getQuery = () => {
	var query = new URLSearchParams(
		window.location.href.substring(
			window.location.href.lastIndexOf("?")+1,
			window.location.href.length
		)
	);
	return query;
};
